import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CarProfile, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import BrandLogo from '../../components/BrandLogo';
import DemoBanner from '../../components/DemoBanner';
import { useSystemInfo } from '../../hooks/useSystemInfo';

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const { brand_name } = useSystemInfo();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    const result = await register({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      role: 'client'
    });
    
    if (result.success) {
      toast.success('Registration successful');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <DemoBanner />
      <div className="flex-1 flex">
      {/* Left Side - Branded */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0c1a2e]">
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <BrandLogo size={224} alt={brand_name} className="mb-6" />
          <h1 className="text-2xl font-bold tracking-tight text-white mb-3">Join {brand_name}</h1>
          <p className="text-base text-white/70 max-w-sm text-center">
            Create your account to track your car imports, upload documents, and stay updated on your shipments.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo size={48} alt={brand_name} />
            <span className="font-bold text-xl tracking-tight">Order Manager</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight">{t('auth.createAccount')}</h2>
            <p className="text-muted-foreground mt-2">Fill in your details to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.name')}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                className="input-brutalist"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className="input-brutalist"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('auth.phone')}</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+213 555 123 456"
                className="input-brutalist"
                data-testid="register-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className="input-brutalist pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="input-brutalist"
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary mt-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : t('auth.signUp')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default RegisterPage;
